import { Fragment, useEffect, useState, useRef, type FormEvent } from 'react';
import { Send, ArrowUpRight, MessageCircle, RefreshCw, Check, Clock, ChevronDown, X, Minus, Bell, BellOff } from 'lucide-react';
import { api, send } from './api';
import {contactHints} from '../shared/contact-hints';
const ui: any = {
  en: [
    'Type your message…',
    'Talk to our team',
    'Your name',
    'Email',
    'Phone',
    'I agree to be contacted about my enquiry.',
    'Send contact details',
    'Continue on WhatsApp',
    'Your handoff request is saved. Use WhatsApp or share contact details to continue.',
    'Contact details saved.',
    'Share your contact details',
  ],
  ur: [
    'اپنا پیغام لکھیں…',
    'ہماری ٹیم سے بات کریں',
    'آپ کا نام',
    'ای میل',
    'فون',
    'میں اپنی درخواست کے بارے میں رابطے کی اجازت دیتا ہوں۔',
    'رابطہ معلومات بھیجیں',
    'واٹس ایپ پر بات کریں',
    'رابطے کی درخواست محفوظ ہو گئی۔ واٹس ایپ یا رابطے کی معلومات سے بات آگے بڑھائیں۔',
    'رابطہ معلومات محفوظ ہو گئیں۔',
    'رابطہ معلومات دیں',
  ],
  roman: [
    'Apna message likhein…',
    'Hamari team se baat karein',
    'Aap ka naam',
    'Email',
    'Phone',
    'Main apni enquiry ke bare mein contact ki ijazat deta hon.',
    'Contact details bhejein',
    'WhatsApp par baat karein',
    'Rabta ki request save ho gayi. WhatsApp ya contact details se baat aagay barhayein.',
    'Contact details save ho gayi hain.',
    'Apni contact details dein',
  ],
  ar: [
    'اكتب رسالتك…',
    'تحدث مع فريقنا',
    'الاسم',
    'البريد الإلكتروني',
    'الهاتف',
    'أوافق على التواصل معي بخصوص استفساري.',
    'إرسال بيانات الاتصال',
    'متابعة عبر واتساب',
    'تم استلام طلبك.',
    'تم حفظ بيانات الاتصال.',
    'شارك بيانات الاتصال',
  ],
  fr: [
    'Votre message…',
    'Contacter notre équipe',
    'Votre nom',
    'E-mail',
    'Téléphone',
    'J’accepte d’être contacté concernant ma demande.',
    'Envoyer mes coordonnées',
    'Continuer sur WhatsApp',
    'Votre demande a été reçue.',
    'Coordonnées enregistrées.',
    'Partager mes coordonnées',
  ],
  zh: [
    '输入您的消息…',
    '联系我们的团队',
    '姓名',
    '邮箱',
    '电话',
    '我同意就此咨询与我联系。',
    '发送联系方式',
    '通过 WhatsApp 继续',
    '团队已收到您的请求。',
    '联系方式已保存。',
    '留下联系方式',
  ],
  es: [
    'Su mensaje…',
    'Contactar al equipo',
    'Nombre',
    'Correo',
    'Teléfono',
    'Acepto ser contactado sobre mi consulta.',
    'Enviar datos',
    'Continuar en WhatsApp',
    'Solicitud recibida.',
    'Datos guardados.',
    'Compartir datos',
  ],
  de: [
    'Ihre Nachricht…',
    'Team kontaktieren',
    'Name',
    'E-Mail',
    'Telefon',
    'Ich stimme der Kontaktaufnahme zu meiner Anfrage zu.',
    'Kontaktdaten senden',
    'Weiter auf WhatsApp',
    'Anfrage erhalten.',
    'Kontaktdaten gespeichert.',
    'Kontaktdaten teilen',
  ],
};
type Session = { conversationId: string; token: string };
type ChatMessage={role:string;content:string;created_at?:string;pending?:boolean;retryText?:string};
export function Widget() {
  const site = new URLSearchParams(location.search).get('site') || 'silver-bell';
  const [config, setConfig] = useState<any>();
  const [session, setSession] = useState<Session>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [language, setLanguage] = useState('en');
  const [busy, setBusy] = useState(false);
  const [replying,setReplying]=useState(false);
  const [error, setError] = useState('');
  const [handoff, setHandoff] = useState(false);
  const [contact, setContact] = useState(false);
  const [notice, setNotice] = useState('');
  const [replyProvider,setReplyProvider]=useState('');
  const [wa, setWa] = useState<string>();
  const end = useRef<HTMLDivElement>(null);
  const feed=useRef<HTMLDivElement>(null);
  const nearBottom=useRef(true);
  const composer=useRef<HTMLInputElement>(null);
  const [showScroll,setShowScroll]=useState(false);
  const [visitorName,setVisitorName]=useState('');
  const [nameDraft,setNameDraft]=useState('');
  const [nameSkipped,setNameSkipped]=useState(false);
  const [contactDraft,setContactDraft]=useState({email:'',phone:''});
  const [verificationContact,setVerificationContact]=useState(false);
  const [sound,setSound]=useState(true);
  const soundRef=useRef<AudioContext|null>(null);
  const soundEnabled=useRef(true);
  const notifyAllowed=useRef(true);
  const embedded=window.parent!==window;
  useEffect(()=>{if(embedded)return;const monitor=document.createElement('script');monitor.src=location.origin+'/visitor-monitor.js?v=23';monitor.setAttribute('data-site-id',site);document.head.appendChild(monitor);return ()=>monitor.remove();},[site]);
  const mobileCopy=language==='roman'?{close:'Chat band karein',reset:'Nayi chat',retry:'Dobara koshish karein',privacy:'AI ke messages Google process karta hai. Zaati ya hassas maloomat na bhejein.',limited:'AI jawab filhal available nahi',send:'Message bhejein',network:'Message nahi bheja ja saka. Dobara koshish karein.'}:language==='ur'?{close:'چیٹ بند کریں',reset:'نئی چیٹ',retry:'دوبارہ کوشش کریں',privacy:'AI کے پیغامات Google پراسیس کرتا ہے۔ حساس معلومات نہ بھیجیں۔',limited:'AI جواب فی الحال دستیاب نہیں',send:'پیغام بھیجیں',network:'پیغام نہیں بھیجا جا سکا۔ دوبارہ کوشش کریں۔'}:{close:'Close chat',reset:'New conversation',retry:'Try again',privacy:'Google processes AI messages. Please avoid sensitive information.',limited:'AI replies temporarily unavailable',send:'Send message',network:'Your message could not be sent. Please try again.'};
  const directWa=config?.whatsappUrl?config.whatsappUrl+'?text='+encodeURIComponent(config.name+' — '+messages.filter(m=>m.role==='user'&&!m.pending).slice(-4).map(m=>m.content).join('\n').slice(0,1800)):undefined;
  const t = ui[language] || ui.en;
  const key = 'atlas-session-' + site;
  const request = (route: string, data: any = {}, signal?:AbortSignal) =>
    api('widget/' + route, {
      method: 'POST',
      signal,
      headers: { Authorization: 'Bearer ' + session?.token },
      body: JSON.stringify({ conversationId: session?.conversationId, ...data }),
    });
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        let saved: Session | undefined;
        try {
          saved = JSON.parse(sessionStorage.getItem(key) || 'null');
        } catch {}
        if (saved) {
          try {
            const h = await api('widget/history', {
              method: 'POST',
              headers: { Authorization: 'Bearer ' + saved.token },
              body: JSON.stringify({ conversationId: saved.conversationId }),
            });
            if (live) {
              setSession(saved);
              setConfig(h.config);
              setMessages(h.messages.map((message:any,index:number)=>{
                try{const meta=JSON.parse(message.metadata||'{}');return {...message,retryText:meta.provider==='gemini-fallback'&&meta.uncertain&&!meta.needsClarification?h.messages.slice(0,index).filter((m:any)=>m.role==='user').at(-1)?.content:undefined};}catch{return message;}
              }));
              setLanguage(h.language);
              setVisitorName(h.visitorName||'');
              if(h.requestContact){setVerificationContact(true);setContact(true);}
              setHandoff(h.status === 'human');
              const last=h.messages.filter((m:any)=>m.role==='assistant').at(-1);
              try{setReplyProvider(JSON.parse(last?.metadata||'{}').provider||'');}catch{}
            }
            return;
          } catch {}
        }
        const s = await send('widget/session', { siteId: site });
        if (live) {
          setSession(s);
          setConfig(s.config);
          try {
            sessionStorage.setItem(key, JSON.stringify(s));
          } catch {
            /* Tab-local state still works when third-party storage is blocked. */
          }
        }
      } catch (e) {
        if (live) setError((e as Error).message);
      }
    })();
    return () => {
      live = false;
    };
  }, [site, key]);
  useEffect(()=>{
    if(!session||!config)return;
    const timezone=Intl.DateTimeFormat().resolvedOptions().timeZone||'';
    const base={timezone,locale:navigator.language||''};
    const save=(data:any)=>{void api('widget/context',{method:'POST',headers:{Authorization:'Bearer '+session.token},body:JSON.stringify({conversationId:session.conversationId,...base,...data})}).catch(()=>{});};
    const host=(event:MessageEvent)=>{
      if(!embedded||event.source!==window.parent||![config.domain,location.origin].includes(event.origin)||event.data?.type!=='atlas:context')return;
      save({page:typeof event.data.page==='string'?event.data.page.slice(0,4096):'',title:typeof event.data.title==='string'?event.data.title.slice(0,200):'',referrer:typeof event.data.referrer==='string'?event.data.referrer.slice(0,4096):''});
    };
    window.addEventListener('message',host);
    save(embedded?{}:{page:location.origin+location.pathname,title:document.title.slice(0,200)});
    if(embedded){window.parent.postMessage({type:'atlas:context-request'},config.domain);if(config.domain!==location.origin)window.parent.postMessage({type:'atlas:context-request'},location.origin);}
    return ()=>window.removeEventListener('message',host);
  },[session?.conversationId,config?.domain]);
  useEffect(()=>{
    if(!session||!config)return;
    let hostOpen=!embedded;
    const pulse=(leaving=false)=>{void api('widget/presence',{method:'POST',keepalive:leaving,headers:{Authorization:'Bearer '+session.token},body:JSON.stringify({conversationId:session.conversationId,visible:!leaving&&hostOpen&&document.visibilityState==='visible'})}).catch(()=>{});};
    const state=(event:MessageEvent)=>{if(event.source!==window.parent||![config.domain,location.origin].includes(event.origin)||event.data?.type!=='atlas:state')return;hostOpen=event.data.open===true;pulse();};
    const visibility=()=>pulse(),leave=()=>pulse(true);
    window.addEventListener('message',state);document.addEventListener('visibilitychange',visibility);window.addEventListener('pagehide',leave);
    if(!embedded)pulse();
    const timer=setInterval(()=>{if(hostOpen&&document.visibilityState==='visible')pulse();},30000);
    return ()=>{clearInterval(timer);window.removeEventListener('message',state);document.removeEventListener('visibilitychange',visibility);window.removeEventListener('pagehide',leave);pulse(true);};
  },[session?.conversationId,config?.domain]);
  useEffect(() => {
    if(nearBottom.current)end.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages, replying, contact]);
  useEffect(()=>{if(config?.chat?.messageSound===false){soundEnabled.current=false;setSound(false);}},[config?.chat?.messageSound]);
  useEffect(()=>{
    const hostState=(event:MessageEvent)=>{if(event.source===window.parent&&event.data?.type==='atlas:state')notifyAllowed.current=event.data.notify!==false;};
    const unlock=()=>{void prepareReplyAudio();};
    window.addEventListener('message',hostState);
    document.addEventListener('pointerdown',unlock);document.addEventListener('keydown',unlock);
    return ()=>{window.removeEventListener('message',hostState);document.removeEventListener('pointerdown',unlock);document.removeEventListener('keydown',unlock);void soundRef.current?.close();};
  },[]);
  async function prepareReplyAudio(){
    if(!soundEnabled.current)return;
    try{soundRef.current ||= new AudioContext();if(soundRef.current.state==='suspended')await soundRef.current.resume();}catch{/* Browser audio restrictions do not change the visitor's mute choice. */}
  }
  async function toggleSound(){
    if(sound){soundEnabled.current=false;setSound(false);return;}
    soundEnabled.current=true;setSound(true);await prepareReplyAudio();
  }
  function playReplySound(){
    const ctx=soundRef.current;if(config?.chat?.messageSound===false||!soundEnabled.current||!notifyAllowed.current||!ctx||ctx.state!=='running')return;
    const tone=ctx.createOscillator(),gain=ctx.createGain();tone.frequency.value=660;gain.gain.setValueAtTime(0.04,ctx.currentTime);gain.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+0.15);tone.connect(gain);gain.connect(ctx.destination);tone.start();tone.stop(ctx.currentTime+0.16);
  }
  async function saveName(e:FormEvent){
    e.preventDefault();if(!session||!nameDraft.trim()||busy)return;setBusy(true);
    try{const result=await request('profile',{name:nameDraft.trim()});setVisitorName(result.name);setNameSkipped(true);}catch{setError(mobileCopy.network);}finally{setBusy(false);}
  }
  useEffect(()=>{
    const viewport=window.visualViewport;
    const resize=()=>document.documentElement.style.setProperty('--chat-height',`${viewport?.height||window.innerHeight}px`);
    const escape=(event:KeyboardEvent)=>{if(event.key==='Escape'&&embedded)window.parent.postMessage({type:'atlas:close'},'*');};
    resize();viewport?.addEventListener('resize',resize);window.addEventListener('resize',resize);
    window.addEventListener('keydown',escape);
    return ()=>{viewport?.removeEventListener('resize',resize);window.removeEventListener('resize',resize);window.removeEventListener('keydown',escape);document.documentElement.style.removeProperty('--chat-height');};
  },[]);
  async function chat(e: FormEvent) {
    e.preventDefault();
    await submitMessage(text);
  }
  async function submitMessage(value:string) {
    if (!value.trim() || !session || busy) return;
    const input = value.trim();
    setText('');
    setBusy(true);
    setReplying(true);
    setError('');
    nearBottom.current=true;
    setMessages(m=>[...m,{role:'user',content:input,created_at:new Date().toISOString(),pending:true}]);
    const controller=new AbortController();
    const deadline=setTimeout(()=>controller.abort(),45000);
    try {
      const r = await request('chat', { message: input },controller.signal);
      setMessages((m) => [
        ...m.map(message=>message.pending?{...message,pending:false}:message),
        { role: 'assistant', content: r.reply,created_at:new Date().toISOString(),retryText:r.retryable?input:undefined },
      ]);
      setLanguage(r.language);
      setHandoff(r.handoff);
      setReplyProvider(r.provider||'');
      playReplySound();
      if(embedded)window.parent.postMessage({type:'atlas:reply',label:r.language==='roman'?'Naya jawab':'New message'},'*');
      // A number submitted to check a caller is not this visitor's contact information.
      const hints=r.verificationRequested||r.provider==='local-verification'?{email:'',phone:''}:contactHints(input);
      if(r.requestContact){setVerificationContact(true);setContactDraft({email:'',phone:''});setContact(true);}
      if(hints.email||hints.phone){setContactDraft(hints);setContact(true);}
    } catch (e) {
      setMessages(m=>m.filter(message=>!message.pending));
      setText(draft=>draft||input);
      setError(mobileCopy.network);
    } finally {
      clearTimeout(deadline);
      setBusy(false);
      setReplying(false);
    }
  }
  async function human() {
    if (!session || busy) return;
    setBusy(true);
    try {
      const r = await request('handoff');
      setHandoff(true);
      setContact(true);
      setWa(r.whatsappUrl || undefined);
      setNotice(t[8]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function lead(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await request('lead', {
        name: f.get('name'),
        email: f.get('email'),
        phone: f.get('phone'),
        consent: f.get('consent') === 'on',
      });
      setContact(false);
      setVerificationContact(false);
      setVisitorName(String(f.get('name')||''));
      setNotice(t[9]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="widget"
      style={{ '--agent-color': '#075e54' } as any}
      dir={['ur', 'ar'].includes(language) ? 'rtl' : 'ltr'}
    >
      <header className="widget-head">
        <div className="agent-symbol"><MessageCircle size={24}/></div>
        <div>
          <strong>{config?.name || 'Chat'}</strong>
          <small>
            {replying?'Typing…':config?.agentName || 'Chat'}
          </small>
        </div>
        {config?.chat?.messageSound!==false&&<button className="widget-close" aria-label={sound?'Mute reply notifications':'Enable reply sound'} title={sound?'Mute reply notifications':'Enable reply sound'} aria-pressed={sound} onClick={toggleSound}>{sound?<Bell size={18}/>:<BellOff size={18}/>}</button>}
        {embedded&&<button className="widget-close" aria-label="Minimize chat" title="Minimize chat" onClick={()=>window.parent.postMessage({type:'atlas:minimize'},'*')}><Minus size={20}/></button>}
        {embedded&&<button className="widget-close" aria-label={mobileCopy.close} title={mobileCopy.close} onClick={()=>window.parent.postMessage({type:'atlas:close'},'*')}><X size={22}/></button>}
      </header>
      <div className="widget-feed" ref={feed} onScroll={()=>{const element=feed.current;if(element){nearBottom.current=element.scrollHeight-element.scrollTop-element.clientHeight<80;setShowScroll(!nearBottom.current);}}} role="log" aria-live="polite" aria-relevant="additions text">
        {config&&<div className="bubble assistant welcome-bubble">{visitorName?config.welcome.includes('{name}')?config.welcome.replaceAll('{name}',visitorName):visitorName+', '+config.welcome:config.welcome.replaceAll('{name}','').replace(/\s+/g,' ').replace(/\s+([!,.؟])/g,'$1').trim()||'Hello! How can we help you?'}</div>}
        {config?.chat?.askVisitorName&&!visitorName&&!nameSkipped&&<form className="name-prompt" onSubmit={saveName}><label>{language==='roman'?'Aap ko kis naam se bulaun?':language==='ur'?'آپ کو کس نام سے بلاؤں؟':'What should we call you?'}<input autoComplete="given-name" value={nameDraft} onChange={e=>setNameDraft(e.target.value)} maxLength={80} aria-label="Your name (optional)" placeholder={t[2]}/></label><button disabled={busy||!nameDraft.trim()}>Continue</button><button type="button" onClick={()=>setNameSkipped(true)}>Skip</button></form>}
        {messages.map((m, i) => (
          <Fragment key={i}>
          {m.created_at&&(!messages[i-1]?.created_at||new Date(messages[i-1].created_at!).toDateString()!==new Date(m.created_at).toDateString())&&<div className="chat-date">{new Date(m.created_at).toLocaleDateString(undefined,{day:'numeric',month:'short'})}</div>}
          <div className={'bubble ' + m.role}>
            <div className="message-text">{m.content}</div>
            <span className="message-meta">{m.created_at&&<time dateTime={m.created_at}>{new Date(m.created_at).toLocaleTimeString(undefined,{hour:'2-digit',minute:'2-digit'})}</time>}{m.role==='user'&&(m.pending?<Clock size={12} aria-label="Sending"/>:<Check size={14} aria-label="Accepted by the assistant"/>)}</span>
            {m.retryText&&i===messages.length-1&&!replying&&<div className="reply-actions"><button disabled={busy} onClick={()=>submitMessage(m.retryText!)}><RefreshCw size={14}/>{mobileCopy.retry}</button>{directWa&&<a href={directWa} target="_blank" rel="noreferrer">WhatsApp <ArrowUpRight size={14}/></a>}</div>}
          </div>
          </Fragment>
        ))}
        {replying&&<div className="bubble assistant typing-bubble" role="status" aria-label="Typing…"><span className="typing-dots"><i/><i/><i/></span><small>Typing…</small></div>}
        {handoff && !replying && !messages.at(-1)?.retryText && (
          <button className="handoff-prompt" onClick={human} disabled={busy}>
            {t[1]} <ArrowUpRight size={16} />
          </button>
        )}
        {notice && (
          <p className="widget-notice">
            <Check size={16} />
            {notice}
          </p>
        )}
        {wa && (
          <a className="wa-link" href={wa} target="_blank" rel="noreferrer">
            {t[7]} <ArrowUpRight size={16} />
          </a>
        )}
        {contact && (
          <form className="contact-form" key={contactDraft.email+'|'+contactDraft.phone} onSubmit={lead}>
            <h3>{t[10]}</h3>
            <input name="name" defaultValue={visitorName} autoComplete="name" required maxLength={100} placeholder={t[2]} aria-label={t[2]} />
            <input name="email" defaultValue={contactDraft.email} autoComplete="email" type="email" placeholder={t[3]} aria-label={t[3]} />
            <input name="phone" defaultValue={contactDraft.phone} required={verificationContact} autoComplete="tel" inputMode="tel" placeholder={t[4]} aria-label={t[4]} />
            {verificationContact&&<small>{language==='roman'?'Apna naam aur number dein. Email optional hai.':language==='ur'?'اپنا نام اور نمبر دیں۔ ای میل اختیاری ہے۔':'Your own name and phone number. Email is optional.'}</small>}
            <label className="consent">
              <input name="consent" type="checkbox" required />
              {t[5]}
            </label>
            <button className="primary" disabled={busy}>
              {t[6]}
            </button>
          </form>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div ref={end} />
      </div>
      {showScroll&&<button className="chat-scroll" aria-label="Latest messages" onClick={()=>{nearBottom.current=true;setShowScroll(false);end.current?.scrollIntoView({behavior:'smooth'});}}><ChevronDown size={20}/></button>}
      <footer className="widget-footer">
        <form onSubmit={chat}>
          <input
            ref={composer}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t[0]}
            aria-label={t[0]}
            maxLength={3000}
            disabled={!session}
          />
          <button aria-label={mobileCopy.send} disabled={!session || busy || !text.trim()}>
            <Send size={18} />
          </button>
        </form>
        <div className="widget-tools"><button className="contact-link" onClick={() => setContact(!contact)}>{t[10]}</button><button className="contact-link" disabled={busy} title={mobileCopy.reset} onClick={()=>{try{sessionStorage.removeItem(key);}catch{}location.reload();}}>{language==='roman'?'Nayi chat':language==='ur'?'نئی چیٹ':'New chat'}</button>{config?.provider==='gemini'&&<details><summary>{language==='roman'?'Privacy':'Privacy'}</summary><p>{mobileCopy.privacy}</p></details>}</div>
      </footer>
    </div>
  );
}
export function Demo() {
  const [site, setSite] = useState('silver-bell');
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '/widget.js';
    script.dataset.siteId = site;
    document.body.appendChild(script);
    return () => {
      script.remove();
      document
        .querySelectorAll('iframe[title="Website chat"]')
        .forEach((f) => f.parentElement?.remove());
    };
  }, [site]);
  return (
    <div className="demo">
      <nav>
        <a href="/" className="demo-brand">
          S / B
        </a>
        <a href="/">
          Back to agent workspace <ArrowUpRight size={16} />
        </a>
      </nav>
      <div className="demo-controls">
        <span>LIVE WIDGET DEMO</span>
        <select aria-label="Demo website" value={site} onChange={(e) => setSite(e.target.value)}>
          <option value="silver-bell">Silver Bell Associates</option>
          <option value="buildcraft">Buildcraft Studio</option>
        </select>
      </div>
      <section>
        <p className="eyebrow">
          {site === 'silver-bell' ? 'PROPERTY. PEOPLE. POSSIBILITIES.' : 'DESIGN. BUILD. BELONG.'}
        </p>
        <h1>
          {site === 'silver-bell' ? (
            <>
              Find your next
              <br />
              place to call home.
            </>
          ) : (
            <>
              Your vision.
              <br />
              Thoughtfully built.
            </>
          )}
        </h1>
        <p>Local expertise, personal guidance, and a team that understands what matters to you.</p>
        <div className="demo-art">
          <div className="building b1" />
          <div className="building b2" />
          <div className="building b3" />
          <span>ISLAMABAD & RAWALPINDI</span>
        </div>
        <p className="demo-hint">
          <MessageCircle size={20} /> Open the assistant in the bottom corner. Try English, Urdu or
          Roman Urdu.
        </p>
      </section>
    </div>
  );
}
